import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    Observable,
    Subject,
    forkJoin,
    from,
    of
} from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { MergeAllDestineringsalternativCommand } from "~app/features/planera/commands/MergeAllDestineringsalternativCommand";
import { UpdateArbetsorderExternCommand } from "~app/features/planera/commands/UpdateArbetsorderExternCommand";

import { ActivatedRoute } from "@angular/router";
import { ExternArbetsorderStatus, VirkesorderRadStatus } from "~app/common/models/constants";
import { ArbetsorderEventStore, ArbetsorderEventStoreInfo } from "~app/features/shared/services/arbetsorderEventStore.service";
import { SetExternArbetsorderTransportforetagCommand } from "../../commands/SetExternArbetsorderTransportforetagCommand";
import { SetExternarbetsorderDestineringStatusCommand } from "../../commands/SetExternarbetsorderDestineringStatusCommand";
import { DestineringsRadViewModel } from "../../models/DestineringsRadViewModel";
import { ExternArbetsorderDetailsViewModel } from "../../models/ExternArbetsorderDetailsViewModel";
import { ITransportforetag } from "../../models/ITransportforetag";
import { ExternArbetsorderService } from "../../services/externArbetsorder.service";

@Component({
    selector: "rask-extern-arbetsorder-details",
    templateUrl: "./extern-arbetsorder-details.component.html",
    styleUrls: ["./extern-arbetsorder-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: []
})
export class ExternArbetsorderDetailsComponent implements OnInit, OnDestroy {
    arbetsorderId: number;
    externArbetsorderDetails: ExternArbetsorderDetailsViewModel;

    protected transportforetagList: Observable<ITransportforetag[]>;

    protected readonly activatedRoute = inject(ActivatedRoute);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly externArbetsorderService = inject(ExternArbetsorderService);
    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);

    private readonly commandManager: ICommandManager;
    private readonly ngUnsubscribe$ = new Subject<void>();
    constructor() {
        this.commandManager = new CommandManager();
    }
    transportforetagSearchFn = (val: string, item: ITransportforetag) => item.namn?.toUpperCase().includes(val?.toUpperCase()) ?? false;
    formatter = (t: ITransportforetag) => t?.namn;
    outputFormatter = (t: ITransportforetag) => t ? t.id : undefined;

    ngOnInit(): void {
        this.arbetsorderId = +this.activatedRoute.snapshot.paramMap.get("id");
        forkJoin([
            this.externArbetsorderService.getAllTransportforetag(),
            this.externArbetsorderService.getExternarbetsorderDetails(this.arbetsorderId)
        ]).subscribe(([
            transportforetag,
            externarbetsorder
        ]) => {
            this.externArbetsorderDetails = new ExternArbetsorderDetailsViewModel(externarbetsorder);
            this.transportforetagList = of(transportforetag);
        });

        this.arbetsorderEventStore.arbetsorderSynchFinished$
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(x => x.arbetsorderId === this.arbetsorderId)
            ).subscribe(eventInfo => {
                this.updateViol3Status(eventInfo);
            });

        this.arbetsorderEventStore.arbetsorderSendStatusChanged$
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(x => x.arbetsorderId === this.arbetsorderId)
            ).subscribe(eventInfo => {
                this.updateViol3Status(eventInfo);
            });

        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorderId]);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        this.arbetsorderEventStore.removeArbetsorderList([this.arbetsorderId]);
    }

    transportforetagChanged($event: any) {
        if ($event !== this.externArbetsorderDetails.transportforetagId) {
            this.commandManager.addOrUpdate(new SetExternArbetsorderTransportforetagCommand((instance) => {
                instance.arbetsorderId = this.arbetsorderId;
                instance.transportforetagId = $event;
            }), c => c.$type === SetExternArbetsorderTransportforetagCommand.$type);
        }
    }

    save() {
        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService.addWork(work)
            .subscribe(() => {
                this.update();
                this.commandManager.clear();
            });
    }

    canSave() {
        return this.commandManager.isDirty();
    }

    canUpdate(): boolean {
        return !this.commandManager.isDirty();
    }

    update() {
        this.commandService.addTransactionCommand(new UpdateArbetsorderExternCommand(x => x.arbetsorderId = this.arbetsorderId));
        this.commandService.addTransactionCommand(new MergeAllDestineringsalternativCommand(x => x.arbetsorderId = this.arbetsorderId));
        this.busyService.addWork(this.commandService.pushCommandQueueTransaction()).subscribe(x => {
            this.commandService.reset();
            this.externArbetsorderService.getExternarbetsorderDetails(this.arbetsorderId).subscribe(y => {
                this.externArbetsorderDetails = new ExternArbetsorderDetailsViewModel(y);
            });
        });
    }

    disableIgnorera(row: DestineringsRadViewModel): boolean {
        return row.status === ExternArbetsorderStatus.Ok || row.status === ExternArbetsorderStatus.EjBeraknad || row.voStatusRad === VirkesorderRadStatus.Vilande;
    }

    getIcon(row: ExternArbetsorderDetailsViewModel): string {
        return this.externArbetsorderService.getIcon(row.status, row.viol3Dirty, row.viol3Fel);
    }

    getIconRow(row: DestineringsRadViewModel): string {
        return this.externArbetsorderService.getIcon(row.status, false, false);
    }

    toggleStatus(checked: boolean, row: DestineringsRadViewModel) {
        let status = "";
        if (checked) {
            status = ExternArbetsorderStatus.Ignorerad;
        } else {
            status = ExternArbetsorderStatus.EjBeraknad;
        }
        this.commandManager.addOrUpdate(new SetExternarbetsorderDestineringStatusCommand((instance) => {
            instance.externDestineringskopplingId = row.externDestineringskopplingId;
            instance.status = status;
        }), c => c.$type === SetExternarbetsorderDestineringStatusCommand.$type && c.externDestineringskopplingId === row.externDestineringskopplingId);
    }

    private updateViol3Status(eventInfo: ArbetsorderEventStoreInfo) {
        if (!this.externArbetsorderDetails || !eventInfo) {
            return;
        }

        this.externArbetsorderDetails.viol3Dirty = eventInfo.arbetsorderIsDirty || eventInfo.produktionsdelIsDirty || eventInfo.transportdelIsDirty;
        this.externArbetsorderDetails.viol3Fel = eventInfo.hasTransmissionErrorViol3;
    }

}
